/**
 * SOVRANOVA Dashboard v5 — Organic redesign (Board D1/M1, approved 2026-08-13)
 * - Same features and behavior as v4: always-listening Companion, guidance,
 *   daily check-in, Live, AI Session + Vent, recommended, full library, stats.
 * - New: Organic design language (cream/terracotta/sage, Caprasimo/Figtree,
 *   pills, Lucide-style line icons) and a two-column desktop layout with a
 *   quiet right rail. Styles live in styles/organic.css (.og-*).
 * - AmbientField is retired visually by the opaque Organic ground; the
 *   component is no longer mounted here (behavioral logic unaffected).
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { api } from '../../services/api'
import { HealingSessionPlayer } from '../session/HealingSessionPlayer'
import { DashboardCompanion } from './DashboardCompanion'
import { CheckInCard } from '../checkin/CheckInCard'
import { useSettings } from '../../hooks/useSettings'

// Daily check-in + compassionate streak (System 2) — OFF unless enabled at
// build time. With both unset the dashboard is byte-identical to before.
const FEATURE_CHECKINS = import.meta.env.VITE_FEATURE_CHECKINS === 'true'
const FEATURE_STREAKS  = import.meta.env.VITE_FEATURE_STREAKS  === 'true'
// TONE TILES — the SESSIONS list below (sine tone + breath pacer via
// HealingSessionPlayer). OFF unless enabled at build time. Disabled for beta
// wave 1 (pre-beta audio/claims audit, 4 Sep 2026, founder decision D-A): the
// tiles carried clinical-modality names and Hz values for what is a tone and a
// timer. Off = Recommended card and quick action point to Guided Sessions and
// the Session library section is not rendered. Code kept; flip to relist.
const FEATURE_TONE_TILES = import.meta.env.VITE_FEATURE_TONE_TILES === 'true'

const SESSIONS = [
  { id: 'morning_reset',        name: 'Morning Reset',        hz: 528, durationMinutes: 12, type: 'BREATHWORK', icon: 'sun',      desc: '12 min breathwork' },
  { id: 'hypnosis',             name: 'Abundance',            hz: 7,   durationMinutes: 22, type: 'HYPNOSIS',   icon: 'spiral',   desc: 'Settle into abundance' },
  { id: 'sleep',                name: 'Sleep Protocol',       hz: 432, durationMinutes: 30, type: 'SLEEP',      icon: 'moon',     desc: "Tonight's session" },
  { id: 'inner_child',          name: 'Inner Child',          hz: 396, durationMinutes: 18, type: 'SOMATIC',    icon: 'heart',    desc: 'Nervous system chosen' },
  { id: 'nervous_system',       name: 'Nervous System Reset', hz: 528, durationMinutes: 10, type: 'VAGUS',      icon: 'zap',      desc: 'Regulate & restore' },
  { id: 'subconscious',         name: 'Theta Relaxation',     hz: 7,   durationMinutes: 20, type: 'HYPNOSIS',   icon: 'spiral',   desc: 'Deep theta wind-down' },
  { id: 'body_release',         name: 'Body Release',         hz: 396, durationMinutes: 15, type: 'SOMATIC',    icon: 'wind',     desc: 'Release what is held' },
  { id: 'bilateral_processing', name: 'Bilateral Processing', hz: 8,   durationMinutes: 20, type: 'SOMATIC',    icon: 'repeat',   desc: 'EMDR-adjacent' },
  { id: 'thought_defusion',     name: 'Thought Defusion',     hz: 417, durationMinutes: 14, type: 'REFLECTION', icon: 'leaf',     desc: 'Unhook from mind (ACT)' },
  { id: 'distress_tolerance',   name: 'Crisis Reset',         hz: 396, durationMinutes: 12, type: 'VAGUS',      icon: 'shield',   desc: 'TIPP skills (DBT)' },
  { id: 'grief_processing',     name: 'Grief Space',          hz: 396, durationMinutes: 22, type: 'SOMATIC',    icon: 'wave',     desc: 'Let loss move through' },
  { id: 'anger_discharge',      name: 'Anger Discharge',      hz: 417, durationMinutes: 12, type: 'SOMATIC',    icon: 'flame',    desc: 'Safe somatic release' },
  { id: 'self_compassion',      name: 'Self Compassion',      hz: 528, durationMinutes: 15, type: 'REFLECTION', icon: 'heart',    desc: 'Kindness turned inward' },
  { id: 'values_compass',       name: 'Values Compass',       hz: 852, durationMinutes: 18, type: 'REFLECTION', icon: 'compass',  desc: 'Clarify what matters' },
  { id: 'gratitude_activation', name: 'Gratitude Practice',   hz: 528, durationMinutes: 10, type: 'REFLECTION', icon: 'star',     desc: 'Settle into the good' },
  { id: 'polyvagal_titration',  name: 'Polyvagal Titration',  hz: 528, durationMinutes: 16, type: 'VAGUS',      icon: 'leaf',     desc: 'Ladder up to safety' },
  { id: 'shadow_work',          name: 'Shadow Integration',   hz: 396, durationMinutes: 20, type: 'REFLECTION', icon: 'moon',     desc: 'Meet what is hidden' },
  { id: 'morning_intention',    name: 'Morning Intention',    hz: 963, durationMinutes: 8,  type: 'REFLECTION', icon: 'sun',      desc: "Set the day's frequency" },
  { id: 'theta_deep',           name: 'Theta Deep Rest',      hz: 6,   durationMinutes: 25, type: 'HYPNOSIS',   icon: 'spiral',   desc: 'Slow theta, deeply restful' },
]
// Quick daily actions. 'journal' is a NAV action (real reflective journal), not a frequency session.
// With the tone tiles off, the quick grid offers Guided Sessions (a real spoken
// session) in place of the tone timers. 'NAV_HYPNOSIS' is a NAV action.
const GUIDED_QUICK = { id: 'guided', name: 'Guided Sessions', type: 'NAV_HYPNOSIS', icon: 'spiral', desc: 'A spoken session, made for you' }
const QUICK = (FEATURE_TONE_TILES ? [
  SESSIONS.find(s => s.id === 'morning_reset'),
  SESSIONS.find(s => s.id === 'hypnosis'),
  { id: 'journal', name: 'Journal', type: 'NAV_JOURNAL', icon: 'book', desc: 'Reflect & write' },
  SESSIONS.find(s => s.id === 'sleep'),
] : [
  GUIDED_QUICK,
  { id: 'journal', name: 'Journal', type: 'NAV_JOURNAL', icon: 'book', desc: 'Reflect & write' },
]).filter(Boolean)
const RECOMMENDED = SESSIONS.find(s => s.id === 'inner_child') || SESSIONS[3]
// All other sessions, grouped by therapeutic category, shown in the library
const LIBRARY_GROUPS = [
  { label: 'Somatic & Body', types: ['SOMATIC'] },
  { label: 'Nervous System', types: ['VAGUS'] },
  { label: 'Deep Relaxation & Theta', types: ['HYPNOSIS'] },
  { label: 'Reflection', types: ['REFLECTION'] },
  { label: 'Breathwork & Sleep', types: ['BREATHWORK', 'SLEEP'] },
]

// Lucide-style line icons (stroke 2, currentColor) — replaces the emoji tiles.
const IC = {
  sun:     <><circle cx="12" cy="12" r="4"/><path d="M12 2v2m0 16v2M4.9 4.9l1.4 1.4m11.4 11.4 1.4 1.4M2 12h2m16 0h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4"/></>,
  moon:    <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z"/>,
  spiral:  <><path d="M12 3a9 9 0 1 0 9 9"/><path d="M12 3v9l6-3"/></>,
  book:    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20V2H6.5A2.5 2.5 0 0 0 4 4.5v15Z"/>,
  star:    <path d="m12 3 1.9 5.8H20l-4.9 3.6 1.9 5.8-5-3.6-5 3.6 1.9-5.8L4 8.8h6.1Z"/>,
  heart:   <path d="M19 14c1.5-1.5 3-3.2 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.8 0-3 .5-4.5 2-1.5-1.5-2.7-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4 3 5.5l7 7Z"/>,
  zap:     <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8Z"/>,
  wind:    <path d="M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2m7.6-8.6A2 2 0 1 1 11 7H2m10.6 10.6A2 2 0 1 0 14 14H2"/>,
  repeat:  <><path d="m17 2 4 4-4 4"/><path d="M3 11v-1a4 4 0 0 1 4-4h14M7 22l-4-4 4-4"/><path d="M21 13v1a4 4 0 0 1-4 4H3"/></>,
  leaf:    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.5 19 2c1 2 2 4.2 2 8 0 5.5-4.8 10-10 10Zm0 0c0-4 1-8 8-12"/>,
  shield:  <path d="M20 13c0 5-3.5 7.5-7.7 9a.6.6 0 0 1-.6 0C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.2 1.2 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1v7Z"/>,
  wave:    <path d="M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"/>,
  flame:   <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5Z"/>,
  compass: <><circle cx="12" cy="12" r="10"/><path d="m16.2 7.8-2 6.3-6.4 2.1 2-6.3 6.4-2.1Z"/></>,
  chat:    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z"/>,
  mic:     <><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></>,
  phones:  <><path d="M3 14h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Zm18 0h-3a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-5Z"/><path d="M21 14v-2a9 9 0 0 0-18 0v2"/></>,
}
function Icon({ name, size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display:'block', flex:'none' }}>
      {IC[name] || IC.star}
    </svg>
  )
}

function Sparkline({ data, max, color = '#7a8a5e' }) {
  if (!data || data.length < 2) return null
  const W = 240, H = 48, P = 4
  const pts = data.map((d, i) => ({ x: P + (i/(data.length-1))*(W-P*2), y: P + (1-d.score/max)*(H-P*2) }))
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', maxWidth:W, display:'block' }}>
      <polyline fill="none" stroke={color} strokeWidth="2" points={pts.map(p=>`${p.x},${p.y}`).join(' ')} strokeLinecap="round" strokeLinejoin="round" />
      {pts.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r={i===pts.length-1?4:2.5} fill={color} />)}
    </svg>
  )
}
function sev(score, max) {
  const p = score/max
  if (p < 0.15) return '#56633f'; if (p < 0.37) return '#b2622d'
  if (p < 0.56) return '#8c491a'; return '#c0392b'
}
const DEPTH = { initial:'Just beginning', developing:'Building momentum', moderate:'Growing deeper', deep:'Deep knowing', profound:'Profound connection' }
const WISDOMS = [
  '"The pattern you most want to change is the one closest to breaking."',
  '"Healing is not linear — it is a spiral returning to wholeness."',
  '"Your nervous system is not broken. It learned to survive."',
  '"Rest is not the absence of healing. It is where healing lives."',
]

export function Dashboard({ onStartSession, onStartVent, onStartAssessment, onStartHypnosis, onStartJournal, onStartAffirmations, onStartLive }) {
  const { settings } = useSettings()
  const [memory, setMemory] = useState(null)
  const [outcomes, setOutcomes] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeSession, setActiveSession] = useState(null)
  const [wisdom] = useState(() => WISDOMS[Math.floor(Math.random() * WISDOMS.length)])
  const [welcome, setWelcome] = useState(null)
  // /welcome is the slowest call in the app (Opus + ElevenLabs). Until it
  // settles the Companion must not speak, or it greets with the generic
  // fallback and then talks over itself when the real greeting lands.
  const [welcomeSettled, setWelcomeSettled] = useState(false)
  const [engagement, setEngagement] = useState(null)
  const [guidance, setGuidance] = useState(null)
  const [checkinNonce, setCheckinNonce] = useState(0)
  const [voicePlaying, setVoicePlaying] = useState(false)
  const [showCompanion, setShowCompanion] = useState(settings.companionOnDashboard !== false && !settings.textOnly)
  const audioRef = useRef(null)
  const voiceReadyRef = useRef(false)
  const voicePlayedRef = useRef(false)

  const handleCompanionNavigate = useCallback((action) => {
    setShowCompanion(false)
    if (action === 'HYPNOSIS') onStartHypnosis?.()
    else if (action === 'VENT') onStartVent?.()
    else if (action === 'SESSION') onStartSession?.()
    else if (action === 'JOURNAL') onStartJournal?.()
    else if (action === 'AFFIRMATIONS') onStartAffirmations?.()
  }, [onStartHypnosis, onStartVent, onStartSession, onStartJournal, onStartAffirmations])

  const playVoiceNow = useCallback(() => {
    if (voicePlayedRef.current || !audioRef.current) return
    voicePlayedRef.current = true
    audioRef.current.currentTime = 0
    audioRef.current.play().then(() => setVoicePlaying(true)).catch(() => {})
  }, [])

  useEffect(() => {
    if (!voiceReadyRef.current) return
    const unlock = () => { playVoiceNow(); cleanup() }
    const cleanup = () => {
      document.removeEventListener('click', unlock)
      document.removeEventListener('touchstart', unlock)
      document.removeEventListener('scroll', unlock)
    }
    document.addEventListener('click', unlock, { once: true })
    document.addEventListener('touchstart', unlock, { once: true, passive: true })
    document.addEventListener('scroll', unlock, { once: true, passive: true })
    return cleanup
  }, [voiceReadyRef.current, playVoiceNow])

  // Quietly pre-generate the user's likely next voiced session (their last-used
  // one) with their current memory, so opening it later starts instantly — the
  // whole script already exists and playback flows with no mid-session catch-up.
  // Fire-and-forget: any failure just means the old (streamed) path runs instead.
  useEffect(() => {
    try {
      const last = JSON.parse(localStorage.getItem('sov_last_session') || 'null')
      if (last && last.apiBase === '/meditation' && last.topic) {
        api.post('/meditation/pregenerate', { topic: last.topic, mode: last.mode, minutes: last.minutes }).catch(() => {})
      }
    } catch (_) {}
  }, [])

  useEffect(() => {
    Promise.all([
      api.get('/memory/context').catch(() => null),
      api.get('/outcomes/summary').catch(() => null),
    ]).then(([m, o]) => { setMemory(m); setOutcomes(o); setLoading(false) })
  }, [])

  // Compassionate streak (System 2). Only fetched when enabled; a failure is
  // silent so the dashboard never depends on it. Re-reads after a check-in.
  useEffect(() => {
    if (!FEATURE_STREAKS) return
    api.get('/engagement').then(setEngagement).catch(() => setEngagement(null))
  }, [checkinNonce])

  // Where you left off + what might help next. Re-reads after a check-in, since
  // a fresh check-in is the strongest signal the suggestion has. Silent on
  // failure — guidance is a nicety and must never break the dashboard.
  useEffect(() => {
    api.get('/guidance/next').then(setGuidance).catch(() => setGuidance(null))
  }, [checkinNonce])

  useEffect(() => {
    api.get('/welcome').then(result => {
      setWelcome(result)
      setWelcomeSettled(true)
      // Publish the dependency-caution flag for AiDisclosure, which no longer
      // fetches /welcome itself (that's the most expensive endpoint in the
      // app — Claude Opus + ElevenLabs synthesis — and this is the single
      // shared success handler, so every /welcome call updates the banner).
      window.dispatchEvent(new CustomEvent('sovranova:dependency-caution', { detail: { caution: !!result?.dependency_caution } }))
      // Respect voice settings — no audio in text-only mode or when voice is off.
      // When the Companion is on the dashboard it owns the voice (it plays this
      // exact clip), so the Dashboard must NOT also play it — that caused the
      // greeting to be spoken twice / overlapping.
      const companionWillSpeak = settings.companionOnDashboard !== false && !settings.textOnly
      if (result?.audio && !settings.textOnly && settings.voiceEnabled && !companionWillSpeak) {
        try {
          const binary = atob(result.audio)
          const bytes = new Uint8Array(binary.length)
          for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
          const blob = new Blob([bytes], { type: result.mimeType || 'audio/mpeg' })
          const audio = new Audio(URL.createObjectURL(blob))
          audio.onended = () => setVoicePlaying(false)
          audioRef.current = audio
          voiceReadyRef.current = true
          audio.play().then(() => { voicePlayedRef.current = true; setVoicePlaying(true) }).catch(() => { setWelcome(r => ({ ...r })) })
        } catch {}
      }
    }).catch(() => setWelcomeSettled(true))   // failed — let the Companion use its own opening
  }, [])

  if (activeSession) return <HealingSessionPlayer session={activeSession} onClose={() => setActiveSession(null)} onComplete={() => setActiveSession(null)} />
  if (loading) return <div className="dashboard"><div className="loading-mark">◎</div></div>

  const sessionCount = memory?.sessionCount || 0
  const depthLabel = DEPTH[memory?.memoryDepth || 'initial'] || 'Just beginning'
  // ONE streak on screen. When the compassionate streak (System 2) is enabled
  // it wins — it has grace days, is opt-out, and de-escalates under the
  // dependency breaker (so an opted-out or suppressed user sees no streak at
  // all). Otherwise fall back to the session-based consecutive-day streak from
  // /outcomes/summary, which is a genuine day streak.
  // NOTE: the engagement streak currently counts CHECK-INS only — wiring
  // session completions into it is the immediate follow-up (see CURRENT_STATE).
  const engagementActive = FEATURE_STREAKS && engagement && !engagement.opted_out && !engagement.nudge_suppressed
  const streak = engagementActive ? (engagement.streak_count || 0) : (outcomes?.streak || 0)
  const phq9History = outcomes?.phq9History || []
  const gad7History = outcomes?.gad7History || []
  const latestPHQ9 = phq9History[phq9History.length - 1]
  const latestGAD7 = gad7History[gad7History.length - 1]
  const isFirstVisit = phq9History.length === 0
  const assessmentDue = outcomes?.assessmentDue !== false
  const hour = new Date().getHours()
  const tod = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'
  const userName = memory?.preferredName?.split(' ')[0] || null

  return (
    <div className="dashboard">
      <div className="og-cols">
        <div className="og-main">

          <div>
            <p className="og-tod">{tod}</p>
            <h2 className="og-name">{memory?.preferredName ? `Welcome back, ${memory.preferredName.split(' ')[0]}.` : 'Your Journey'}</h2>
          </div>

          {/* ALWAYS-LISTENING AI COMPANION (respects settings) */}
          {showCompanion && settings.companionOnDashboard && !settings.textOnly && (
            <DashboardCompanion
              userName={userName}
              welcomeGreeting={welcome?.greeting}
              welcomeAudio={welcome?.audio}
              welcomeMimeType={welcome?.mimeType}
              welcomePending={!welcomeSettled}
              autoSpeak={settings.voiceEnabled !== false}
              onNavigate={handleCompanionNavigate}
              onDismiss={() => setShowCompanion(false)}
            />
          )}

          {/* WHERE YOU LEFT OFF + WHAT MIGHT HELP NEXT.
              Absent entirely when the dependency breaker is on, or when someone is
              flagged in crisis — the server returns nothing in both cases, so this
              renders nothing. It is an offer, never a demand. */}
          {(guidance?.resume || guidance?.suggestion) && (
            <div className="og-card og-guidance">
              {guidance.resume && (
                <button className="og-resume" onClick={() => onStartSession?.()}>
                  <span className="og-resume-dot" />
                  <span style={{ flex: 1, textAlign: 'left' }}>
                    <span className="og-resume-title">{guidance.resume.label}</span>
                    <span className="og-resume-meta">Pick it back up where you left it</span>
                  </span>
                  <span className="og-chev">›</span>
                </button>
              )}
              {guidance.suggestion && (
                <div className={guidance.resume ? 'og-suggest-below' : ''}>
                  <p className="og-kicker og-suggest-label">A thought</p>
                  <p className="og-suggest-title">{guidance.suggestion.title}</p>
                  <p className="og-suggest-reason">{guidance.suggestion.reason}</p>
                  {guidance.suggestion.action && (
                    <button className="og-suggest-btn"
                      onClick={() => handleCompanionNavigate(guidance.suggestion.action)}>
                      Open this
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* STATIC WELCOME (shown when companion hidden) */}
          {(!showCompanion || !settings.companionOnDashboard || settings.textOnly) && welcome?.greeting && (
            <div className="og-card og-welcome">
              <div style={{ display:'flex', gap:'0.85rem', alignItems:'flex-start' }}>
                <div className="og-voice-ind">
                  {voicePlaying
                    ? <><span className="og-eq e1"/><span className="og-eq e2"/><span className="og-eq e3"/></>
                    : <Icon name="mic" size={17} />}
                </div>
                <div style={{ flex:1 }}>
                  <p className="og-kicker" style={{ color:'var(--og-a700)' }}>Sovranova · {tod.replace('Good ','')}</p>
                  <p className="og-welcome-text">{welcome.greeting}</p>
                  {welcome.sessionCount > 0 && (
                    <p className="og-welcome-meta">{welcome.sessionCount} session{welcome.sessionCount !== 1 ? 's' : ''}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {FEATURE_CHECKINS && (
            <CheckInCard onDone={() => setCheckinNonce(n => n + 1)} />
          )}

          {onStartLive && (
            <button className="og-live" onClick={onStartLive}>
              <span className="og-disc"><Icon name="phones" size={20} /></span>
              <div style={{ flex:1 }}>
                <p className="og-card-t">Sovranova Live</p>
                <p className="og-card-d">Hands-free voice — I hear how you feel</p>
              </div>
              <span className="og-new">NEW</span>
            </button>
          )}

          <div className="og-actions">
            <button className="og-action" onClick={onStartSession}>
              <span className="og-disc"><Icon name="chat" size={20} /></span>
              <div><p className="og-card-t">AI Session</p><p className="og-card-d">Talk with Sovranova</p></div>
            </button>
            <button className="og-action" onClick={onStartVent || onStartSession}>
              <span className="og-disc sage"><Icon name="mic" size={20} /></span>
              <div><p className="og-card-t">Vent</p><p className="og-card-d">Just let it out</p></div>
            </button>
          </div>

          {FEATURE_TONE_TILES ? (
            <div className="og-card og-rec" onClick={() => setActiveSession(RECOMMENDED)}>
              <div className="og-blob" />
              <p className="og-kicker">Recommended for you</p>
              <p className="og-rec-name">{RECOMMENDED.name}</p>
              <p className="og-rec-desc">{RECOMMENDED.type} · {RECOMMENDED.durationMinutes} min — {RECOMMENDED.desc.toLowerCase()}</p>
              <button className="og-cta" style={{ position:'relative' }}>Begin session</button>
            </div>
          ) : (
            <div className="og-card og-rec" onClick={() => onStartHypnosis?.()}>
              <div className="og-blob" />
              <p className="og-kicker">Recommended for you</p>
              <p className="og-rec-name">Guided Session</p>
              <p className="og-rec-desc">GUIDED · 5–20 min — a spoken relaxation session, personalised to you</p>
              <button className="og-cta" style={{ position:'relative' }}>Begin session</button>
            </div>
          )}

          <section>
            <h3 className="og-sect">Your practice today</h3>
            <div className="og-quickgrid">
              {QUICK.map((sess, i) => (
                <button key={sess.id} className="og-quick" onClick={() => {
                  if (sess.type === 'NAV_JOURNAL') { onStartJournal?.(); return }
                  if (sess.type === 'NAV_HYPNOSIS') { onStartHypnosis?.(); return }
                  setActiveSession(sess)
                }}>
                  <span className={`og-disc ${i % 2 === 1 ? 'sage' : ''}`}><Icon name={sess.icon} size={17} /></span>
                  <span className="og-quick-n">{sess.name}</span>
                  <span className="og-quick-d">{sess.desc}</span>
                </button>
              ))}
              <button className="og-quick" onClick={() => onStartAffirmations?.()}>
                <span className="og-disc"><Icon name="star" size={17} /></span>
                <span className="og-quick-n">Affirmations</span>
                <span className="og-quick-d">Personalised, spoken aloud</span>
              </button>
            </div>
          </section>

          {/* FULL SESSION LIBRARY (tone tiles) — rendered only when the tone
              tiles are enabled at build time; off for beta wave 1. */}
          {FEATURE_TONE_TILES && <section>
            <h3 className="og-sect">Session library</h3>
            {LIBRARY_GROUPS.map(group => {
              const groupSessions = SESSIONS.filter(s => group.types.includes(s.type))
              if (groupSessions.length === 0) return null
              return (
                <div key={group.label}>
                  <p className="og-grouplabel">{group.label}</p>
                  {groupSessions.map(sess => (
                    <button key={sess.id} className="og-librow" onClick={() => setActiveSession(sess)}>
                      <span className="og-lib-n">{sess.name}</span>
                      <span className="og-lib-d">{sess.desc} · {sess.durationMinutes} min</span>
                    </button>
                  ))}
                </div>
              )
            })}
          </section>}
        </div>

        {/* ── Right rail: your numbers & wisdom, quiet (Board D1) ── */}
        <div className="og-side">
          {streak > 0 && (
            <div className="og-sidecard og-streak">
              <span className="og-streak-n">{streak}</span>
              <div>
                <p className="og-streak-l">day streak</p>
                <p className="og-streak-sub">{engagementActive ? 'Grace days included — showing up counts.' : 'Consecutive days you showed up.'}</p>
              </div>
            </div>
          )}

          <div className="og-sidecard">
            <p className="og-kicker" style={{ color:'var(--og-a700)' }}>Your journey</p>
            <div className="og-statrow">
              <span className="og-statpill"><b>{sessionCount}</b>{sessionCount === 1 ? 'session' : 'sessions'}</span>
              <span className="og-statpill"><b>{depthLabel}</b></span>
              {latestPHQ9 && <span className="og-statpill"><b style={{ color:sev(latestPHQ9.score,27) }}>{latestPHQ9.score}</b>PHQ-9</span>}
              {latestGAD7 && <span className="og-statpill"><b style={{ color:sev(latestGAD7.score,21) }}>{latestGAD7.score}</b>GAD-7</span>}
            </div>
          </div>

          {assessmentDue && (
            <div className="og-sidecard og-due">
              <p className="og-due-t">Wellbeing check-in {isFirstVisit ? 'recommended' : 'due'}</p>
              <p className="og-due-d">{isFirstVisit ? 'Complete a baseline so Sovranova can track your progress.' : "Let's see where you are now."}</p>
              <button className="og-cta" onClick={onStartAssessment}>{isFirstVisit ? 'Start' : 'Check-in'}</button>
            </div>
          )}

          <div className="og-sidecard og-wisdom">
            <p className="og-kicker">Today's wisdom</p>
            <p className="og-wisdom-q">{wisdom}</p>
          </div>

          {latestPHQ9 && (
            <div className="og-sidecard">
              <p className="og-kicker" style={{ color:'var(--og-s800)' }}>Your wellbeing</p>
              <div style={{ marginTop:'0.75rem' }}>
                {[
                  { label:'Health (PHQ-9)',  pct: Math.round((1-latestPHQ9.score/27)*100) },
                  { label:'Freedom (GAD-7)', pct: Math.round((1-(latestGAD7?.score||0)/21)*100) },
                ].map(p => (
                  <div key={p.label} style={{ marginBottom:'0.7rem' }}>
                    <div style={{ display:'flex', gap:'0.6rem', alignItems:'center', marginBottom:'0.32rem' }}>
                      <span style={{ flex:1, fontSize:'0.82rem', fontWeight:600, color:'var(--og-n800)' }}>{p.label}</span>
                      <span style={{ fontSize:'0.72rem', color:'var(--og-n500)' }}>{p.pct}%</span>
                    </div>
                    <div className="og-track"><div className="og-fill" style={{ width:`${p.pct}%` }}/></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {phq9History.length >= 2 && (
            <div className="og-sidecard">
              <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', marginBottom:'0.6rem' }}>
                <span className="og-kicker" style={{ color:'var(--og-s800)' }}>Mood trend</span>
                <span style={{ fontSize:'0.7rem', color:'var(--og-n500)' }}>Lower is better · max 27</span>
              </div>
              <Sparkline data={phq9History} max={27} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
